import { onReady } from './utils.js';

const players = {};

onReady( function() {
	const youtube_videos = document.querySelectorAll( 'iframe[src^="https://www.youtube.com/embed/"' );

	if ( ! youtube_videos.length ) {
		return;
	}

	/**
	 * Create YT player instance.
	 *
	 * @param {Object} iframe Video iframe element.
	 * @return {Object} YT player instance.
	 */
	function createYTPlayer( iframe ) {
		const url = new URL( iframe.src );
		const video_id = url.pathname.replace( '/embed/', '' );
		return new YT.Player(
			iframe.id,
			{
				origin: `${document.location.protocol}//${document.location.host}`,
				playsinline: 1,
				autoplay: 1,
				videoId: video_id,
			},
		);
	}

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				const iframe = entry.target;

				if ( ! players[ iframe.id ] ) {
					initializePlayer( iframe );
				}

				if ( iframe.dataset.faked !== 'faked' ) {
					return;
				}

				if ( entry.isIntersecting ) {
					try {
						if ( players[ iframe.id ].getPlayerState() !== 1 && iframe.dataset.paused ) {
							players[ iframe.id ].playVideo();
							delete iframe.dataset.paused;
						}
					} catch ( e ) { // eslint-disable-line no-unused-vars
						// If the player is not ready yet, we can't play it.
						console.log( `YouTube player ${iframe.id} is not ready yet.` ); // eslint-disable-line no-console
					}
				} else {
					try {
						if ( players[ iframe.id ].getPlayerState() === 1 ) {
							players[ iframe.id ].pauseVideo();
							iframe.dataset.paused = true;
						}
					} catch ( e ) { // eslint-disable-line no-unused-vars
						// If the player is not ready yet, we can't pause it.
						console.log( `YouTube player ${iframe.id} is not ready yet.` ); // eslint-disable-line no-console
					}
				}
			} );
		},
		{
			threshold: [ 0.5 ],
		},
	);

	const script = document.createElement( 'script' );
	script.src = 'https://www.youtube.com/iframe_api';
	script.type = 'text/javascript';
	script.async = true;
	document.head.appendChild( script );

	const initializePlayer = ( iframe ) => {
		iframe.setAttribute( 'allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share' );

		const url = new URL( iframe.src );
		const params = new URLSearchParams( url.searchParams );
		params.append( 'enablejsapi', '1' );
		url.search = params.toString();
		iframe.src = url.toString();

		iframe.removeAttribute( 'loading' );

		players[ iframe.id ] = createYTPlayer( iframe );
	};

	window.onYouTubeIframeAPIReady = () => {
		youtube_videos.forEach( ( iframe, index ) => {
			if ( ! iframe.id ) {
				iframe.id = `youtube-player-${index}`;
			}

			// Only if not hidden embed, player can't be initialised for hidden iframe.
			if ( iframe.clientWidth > 0 ) {
				initializePlayer( iframe );
			}

			observer.observe( iframe );

			const fakeUserAction = ( event ) => {
				if ( ! players[ iframe.id ] ) {
					return;
				}

				if ( iframe.dataset.faked === 'faked' ) {
					document.body.removeEventListener( event.type, fakeUserAction );
					return;
				}

				// Already waiting for the fake playback to start.
				if ( iframe.dataset.faking ) {
					return;
				}

				try {
					const player = players[ iframe.id ];

					// Pausing before the first frame is rendered leaves the player as a black box,
					// so wait until the video is really playing, pause it, and only once it reports
					// paused hand control back to the observer.
					let done = false;
					const onStateChange = ( stateEvent ) => {
						// The YT API's removeEventListener is unreliable, so ignore events once finished.
						if ( done ) {
							return;
						}

						if ( stateEvent.data === YT.PlayerState.PLAYING ) {
							player.pauseVideo();
							return;
						}

						if ( stateEvent.data !== YT.PlayerState.PAUSED ) {
							return;
						}

						done = true;
						player.seekTo( 0, true );
						player.unMute();
						delete iframe.dataset.faking;
						iframe.dataset.faked = 'faked';

						// We paused it, so let the observer resume it, and re-check the current visibility.
						iframe.dataset.paused = true;
						observer.unobserve( iframe );
						observer.observe( iframe );
					};

					iframe.dataset.faking = 'true';
					player.addEventListener( 'onStateChange', onStateChange );
					player.mute();
					player.playVideo();

					document.body.removeEventListener( event.type, fakeUserAction );
				} catch ( e ) { // eslint-disable-line no-unused-vars
					delete iframe.dataset.faking;
					// If the player is not ready yet, we can't play it.
					// This can happen if the video is not visible on page load.
					console.log( `YouTube player ${iframe.id} is not ready yet.` ); // eslint-disable-line no-console
				}
			};

			// Fake user interaction with the video to enable auto playing it later.
			[ 'click', 'keypress' ].forEach( ( eventType ) => {
				document.body.addEventListener( eventType, fakeUserAction );
			} );
		} );
	};
} );
